import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteFolder,
  createFile,
  moveFileToWorkspace,
  copyFileToWorkspace,
  updateBitbucketFiles,
  moveFileToTrash,
  updateFile,
  updateFolder,
  moveFolderToWorkspace,
  removeFileAfterMoving,
} from '../features/organization/organizationSlice';
import { findFileInRecursiveFolder, getGitLinkForBitbucket } from '../utils/common';
import { Notification } from '@components/Notification';
import { DELETE_MODAL, RENAME_FILE_MODAL } from '@constants/common';
import { fetchFileDetail } from '@utils/apiCalls';
import { useNavigate } from 'react-router-dom';
import useChangeNavigationOnActiveFile from './useChangeNavigationOnActiveFile';

/**
 * Custom hook for file action handlers
 * @param {Function} closeModal - Function to close modal
 * @returns {Object} File action handlers
 */
const useFileActions = (closeModal) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
  const workspaceFoldersList = useSelector((state) => state.organization?.workspaceFoldersList);
  const workspaceList = useSelector((state) => state.organization?.workspaceList);
  const user = useSelector((state) => state.user?.user);
  const { changeNavigationOnActiveFile } = useChangeNavigationOnActiveFile();

  /**
   * Handle duplicate file operation
   * @param {string} id - ID of the file to duplicate
   */
  const handleDuplicate = useCallback(async (id) => {
    const fileDetail = findFileInRecursiveFolder(workspaceFoldersList, id);

    if ('type' in fileDetail) {
      Notification.error('Invalid operation', 'Can not Duplicate Spec Files');
    } else {
      const response = await fetchFileDetail(id, fileDetail.workspace_id);
      if (response) {
        const body = {
          title: `[DUP] ${fileDetail.title}`,
          icon_id: fileDetail.icon_id,
          data: response?.data,
          meta_info: {},
          workspace_id: fileDetail.workspace_id,
          folder_id: fileDetail.folder_id,
        };

        try {
          const result = await dispatch(createFile(body));
          const { fileRes } = result.payload;
          if (fileRes) {
            changeNavigationOnActiveFile({
              filePath: fileRes?.ref_id,
              fileParent: null,
              isApiFile: null,
              workspaceId: activeWorkspace?.slug,
              inEditMode: true,
            });
          }
          Notification.success('Success', 'File duplicated successfully');
        } catch (error) {
          console.error('Error duplicating file:', error);
          Notification.error('Something went wrong', 'Something went wrong creating Duplicate');
        }
      } else {
        Notification.error('Something went wrong', 'Something went wrong creating Duplicate');
      }
      return;
    }
  });

  /**
   * Handle move file operation
   * @param {string} folder_id - Destination folder ID
   * @param {string} workspace_id - Destination workspace ID
   * @param {string} file_id - ID of the file to move
   */
  const handleMoveFile = useCallback(
    async (folder_id, workspace_id, file_id) => {
      const data = {
        folder_id,
        workspace_id,
      };

      const fileDetail = findFileInRecursiveFolder(workspaceFoldersList, file_id);

      if ('type' in fileDetail) {
        Notification.error('Invalid operation', 'Can not Move Spec Files');
        return;
      }

      const res = await dispatch(moveFileToWorkspace({ data, file_id, folder_id, workspace_id }));

      if (res?.payload?.is_success) {
        Notification.success(`Moved file successfully to new workspace`);
        const workspace = workspaceList?.find((w) => w.id === res?.payload?.data?.workspace_id);
        navigate(`/spaces/${workspace?.slug}/pages/${res?.payload?.data?.ref_id}`);
      }
    },
    [dispatch, workspaceFoldersList, navigate, workspaceList]
  );

  /**
   * Handle move folder operation
   * @param {string} folder_id - Destination folder ID
   * @param {string} workspace_id - Destination workspace ID
   * @param {string} file_id - ID of the folder to move
   */
  const handleMoveFolder = useCallback(
    async (folder_id, workspace_id, file_id) => {
      const folder = findFileInRecursiveFolder(workspaceFoldersList, file_id);
      const body = {
        destination_workspace: workspace_id,
        destination_folder: folder_id,
      };
      const res = await dispatch(
        moveFolderToWorkspace({ body: body, folder_id: file_id, workspace_id: folder?.workspace_id })
      );
      if (res?.payload?.is_success) {
        dispatch(removeFileAfterMoving(folder.id));
        const workspace = workspaceList?.find((w) => w.id === res?.payload?.data?.workspace_id);
        navigate(`/spaces/${workspace?.slug}`);
      }
    },
    [dispatch, workspaceFoldersList, navigate, workspaceList]
  );

  /**
   * Handle copy file operation
   * @param {string} folder_id - Destination folder ID
   * @param {string} workspace_id - Destination workspace ID
   * @param {string} file_id - ID of the file to copy
   */
  const handleCopyFile = useCallback(
    async (folder_id, workspace_id, file_id) => {
      const fileDetail = findFileInRecursiveFolder(workspaceFoldersList, file_id);

      if ('type' in fileDetail) {
        Notification.error('Invalid operation', 'Can not Copy Spec Files');
        return;
      }
      const response = await fetchFileDetail(file_id);

      const body = {
        title: `copy of ${fileDetail.title}`,
        icon_id: fileDetail.icon_id,
        data: response?.data,
        meta_info: {},
        workspace_id: workspace_id,
        folder_id: folder_id,
      };

      const res = await dispatch(copyFileToWorkspace(body));
      if (res?.payload?.is_success) {
        const workspace = workspaceList?.find((w) => w.id === res?.payload?.data?.workspace_id);
        navigate(`/spaces/${workspace?.slug}/pages/${res?.payload?.data?.ref_id}`);
      }
    },
    [dispatch, workspaceFoldersList, navigate, workspaceList]
  );

  /**
   * Handle delete file operation
   * @param {string} id - ID of the file to delete
   * @param {boolean} isFolder - Whether the item is a folder
   */
  const handleDeleteFile = useCallback(
    async (id, isFolder = false) => {
      if (isFolder) {
        dispatch(deleteFolder({ id: id, workspaceId: activeWorkspace?.id })).then(({ payload }) => {
          if (payload && payload?.isFileInsideFolder) navigate(`/spaces/${activeWorkspace?.slug}/`);
        });
      } else if (activeWorkspace?.is_git_based) {
        const bitbucketBody = {
          scm_provider: activeWorkspace?.scm_provider,
          git_link: getGitLinkForBitbucket(activeWorkspace?.git_link),
          files: [
            {
              path: id,
              delete: true,
            },
          ],
          commit_info: {
            commitMessage: `Deleted file on path ${id}`,
            parentSha: null,
            branch: activeWorkspace?.meta_info?.bitbucket_branch_name,
          },
          commit_author: {
            name: user?.username,
            email: user?.email,
          },
          workspace_id: activeWorkspace?.id,
        };
        await dispatch(
          updateBitbucketFiles({
            body: bitbucketBody,
            workspace_id: activeWorkspace?.id,
          })
        );
      } else {
        const out = await dispatch(
          moveFileToTrash({
            id: id,
            workspaceId: activeWorkspace?.id,
          })
        ).unwrap();
        if (out && out?.isSameFileRemoved) {
          navigate(`/spaces/${activeWorkspace?.slug}/`);
        }
      }
      closeModal(DELETE_MODAL);
    },
    [dispatch, activeWorkspace, navigate, user, closeModal]
  );

  /**
   * Handle delete folder operation
   * @param {string} id - ID of the folder to delete
   */
  const handleDeleteFolder = useCallback(
    async (id) => {
      handleDeleteFile(id, true);
    },
    [handleDeleteFile]
  );

  /**
   * Handle rename file operation
   * @param {string} id - ID of the file to rename
   * @param {string} fileName - New file name
   */
  const handleRenameFile = useCallback(
    async (id, fileName) => {
      const body = {
        title: fileName.trim(),
      };
      await dispatch(
        updateFile({
          data: body,
          file_id: id,
          workspace_id: activeWorkspace?.id,
          updateActiveFile: false,
        })
      );
      closeModal(RENAME_FILE_MODAL);
    },
    [dispatch, activeWorkspace, closeModal]
  );

  /**
   * Handle rename folder operation
   * @param {string} id - ID of the folder to rename
   * @param {string} newName - New folder name
   */
  const handleRenameFolder = useCallback(
    async (id, newName) => {
      const folder = findFileInRecursiveFolder(workspaceFoldersList, id);
      const body = {
        title: newName?.trim(),
        icon_id: folder?.icon_id,
        parent_id: folder?.parent_id_id,
        meta_info: folder?.meta_info,
        workspace_id: folder?.workspace_id,
        parent_id: folder?.parent_id_id,
      };

      await dispatch(updateFolder({ data: body, folder_id: id, workspace_id: folder?.workspace_id }));
      closeModal(RENAME_FILE_MODAL);
    },
    [dispatch, workspaceFoldersList, closeModal]
  );

  return {
    handleDuplicate,
    handleMoveFile,
    handleMoveFolder,
    handleCopyFile,
    handleDeleteFile,
    handleDeleteFolder,
    handleRenameFile,
    handleRenameFolder,
  };
};

export default useFileActions;
